"use client";

import { useEffect } from "react";

/**
 * Marks the section the reader is in on every `<ContentsRail>` on the page.
 *
 * ⭐ It renders NOTHING. The lists are server-rendered and already correct
 * without JS; this only adds `aria-current`, so the feature degrades to a
 * working set of anchor links if the component never hydrates.
 *
 * ⛔ A SCROLL LISTENER, NOT AN IntersectionObserver, and that is a decision
 * (spec §5c). An IO fires on entry and exit, so answering "which section am I
 * in" from it means tracking a set of visible headings and picking one —
 * exactly the shape that cost two bugs on the mini-chat reveal gate. "The last
 * heading above the line" is one comparison, has no state, and is right by
 * construction inside a long section where NO heading is visible at all — the
 * case an IO gets wrong unless it is written carefully.
 *
 * ⚠️ Scoped to the ids it was given, never a bare `document.querySelector`.
 * After a client-side navigation the App Router can still hold the previous
 * page's DOM, and a document-wide query reaches into it (measured 2026-09-07).
 */
/**
 * On a long article the rail's contents list scrolls inside itself
 * (`RAIL_CONTENTS_SCROLL`), so the current entry can sit outside its window.
 * Nudge the LIST, never the page: `scrollIntoView` would also scroll the
 * window, fighting the reader's own scroll. A list that does not scroll (the
 * collapsed copy below `lg`, or a short list) is left alone.
 *
 * The list moves only when the current entry nears an edge, and then GLIDES
 * it to the middle (owner, 2026-09-18: the old step jumped by exactly the
 * overflow, a small snap at every section). Centring leaves entries visible on
 * both sides, so the next several section changes need no move at all.
 * Reduced motion keeps the instant jump.
 */
// ⛔ At least the `.scroll-fade-y` fade length (34px, globals.css): at 13 the
// current entry could come to rest inside the faded band, half transparent
// (measured 15px from the edge at 1280x660). Change the two together.
const EDGE_MARGIN = 34;

function keepInView(a: HTMLElement) {
  const box = a.closest("nav");
  if (!box || box.scrollHeight <= box.clientHeight) return;
  const b = box.getBoundingClientRect();
  const r = a.getBoundingClientRect();
  if (r.top >= b.top + EDGE_MARGIN && r.bottom <= b.bottom - EDGE_MARGIN) return;
  const offset = r.top - b.top + r.height / 2 - box.clientHeight / 2;
  const reduce = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
  box.scrollTo({ top: box.scrollTop + offset, behavior: reduce ? "auto" : "smooth" });
}

/**
 * Tells a `.scroll-fade-y` list which edges have more entries beyond them, so
 * only those fade. 1px of slack absorbs sub-pixel scroll positions.
 */
function syncFade(box: HTMLElement) {
  const top = box.scrollTop > 1;
  const bottom = box.scrollTop + box.clientHeight < box.scrollHeight - 1;
  const next = [top && "top", bottom && "bottom"].filter(Boolean).join(" ");
  if (box.dataset.fade !== next) box.dataset.fade = next;
}

export default function ScrollSpy({ ids }: { ids: string[] }) {
  // The faded edges. The list scrolls on its own (the reader's wheel, or
  // `keepInView`) and changes height with the nav (`ContentsRailShell`), so it
  // needs its own scroll listener and a ResizeObserver.
  useEffect(() => {
    if (ids.length === 0) return;
    // Found through THIS page's ids, like the links below (see the header note).
    const boxes = [
      ...new Set(
        [...document.querySelectorAll<HTMLElement>(`[data-contents] a[href="#${CSS.escape(ids[0])}"]`)]
          .map((a) => a.closest<HTMLElement>("nav.scroll-fade-y"))
          .filter((n): n is HTMLElement => n !== null),
      ),
    ];
    if (boxes.length === 0) return;
    const onScroll = (e: Event) => syncFade(e.currentTarget as HTMLElement);
    const ro = new ResizeObserver((entries) => entries.forEach((en) => syncFade(en.target as HTMLElement)));
    for (const box of boxes) {
      syncFade(box);
      box.addEventListener("scroll", onScroll, { passive: true });
      ro.observe(box);
    }
    return () => {
      ro.disconnect();
      for (const box of boxes) box.removeEventListener("scroll", onScroll);
    };
  }, [ids]);

  useEffect(() => {
    if (ids.length === 0) return;

    const links = new Map<string, HTMLAnchorElement[]>();
    for (const id of ids) {
      links.set(id, [
        ...document.querySelectorAll<HTMLAnchorElement>(
          `[data-contents] a[href="#${CSS.escape(id)}"]`,
        ),
      ]);
    }

    let current: string | null = null;
    let frame = 0;

    const apply = () => {
      frame = 0;
      // The offset the anchors themselves use, so "current" flips exactly when
      // a heading reaches the place a jump would have parked it — which is why
      // it reads `--nav-height` and NOT `--sticky-top`. The two diverge the
      // moment the nav hides, and reading the collapsing one put the line at
      // 40px while a jump parked its heading at 151px, so the section the
      // reader had just jumped to was not the one marked current.
      const line =
        parseFloat(
          getComputedStyle(document.documentElement).getPropertyValue("--nav-height"),
        ) || 78;

      let active = ids[0];
      for (const id of ids) {
        const el = document.getElementById(id);
        if (!el) continue;
        if (el.getBoundingClientRect().top <= line + 40) active = id;
        else break;
      }

      if (!active || active === current) return;
      current = active;
      for (const [id, anchors] of links) {
        for (const a of anchors) {
          if (id === active) {
            a.setAttribute("aria-current", "true");
            keepInView(a);
          } else a.removeAttribute("aria-current");
        }
      }
    };

    // rAF-throttled: scroll fires far more often than the highlight can change.
    const onScroll = () => {
      if (frame) return;
      frame = requestAnimationFrame(apply);
    };

    apply();
    window.addEventListener("scroll", onScroll, { passive: true });
    window.addEventListener("resize", onScroll);
    return () => {
      if (frame) cancelAnimationFrame(frame);
      window.removeEventListener("scroll", onScroll);
      window.removeEventListener("resize", onScroll);
    };
    // Re-run when the document changes: this is mounted per page, but a
    // client-side navigation between two articles reuses the component.
  }, [ids]);

  return null;
}
